/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import {
  setProductImpressionsEmpty,
  setProductImpressions,
  setAdditionalPageInfo
} from '../../../analytics/digitalDataBuilder';
import { Analytics } from '../../../analytics/sendAnalyticsEvent';

const getMetaDataValue = (choice, key) => {
  const metaItems = choice?.metaDataItems;
  if (!Array.isArray(metaItems)) {
    return null;
  }

  const metaItem = metaItems.find(
    (item) => item?.name?.toLowerCase?.() === key.toLowerCase()
  );

  return metaItem?.value ?? null;
};

const getChoiceSku = (choice) => {
  const metaSku = getMetaDataValue(choice, 'Sku');
  const sku = metaSku || choice?.sku || choice?.Sku || choice?.itemId;
  return sku ? String(sku) : '';
};

const getAvailabilityStatus = (choice) => {
  const availabilityStatus = {
    shipping: { skuStatus: 'n/a' },
    store: { skuStatus: 'n/a' },
    type: 'online'
  };

  const services = choice?.fulfillment?.fulfillmentOptions?.[0]?.services || [];
  const hasService = (serviceType) => services.some((service) => service?.type === serviceType);

  if (hasService('BODFS')) {
    availabilityStatus.shipping.bodfs = 'available';
  }
  if (hasService('ShipToHome')) {
    availabilityStatus.shipping.sth = 'available';
  }
  if (hasService('BOPIS')) {
    availabilityStatus.store.bopis = 'available';
  }
  if (hasService('BOSS')) {
    availabilityStatus.store.boss = 'available';
  }

  return availabilityStatus;
};

const getBadgeInfo = (choice) => {
  const badges = Array.isArray(choice?.badges) ? choice.badges : [];
  if (badges.length > 0) {
    return badges.map((badge) => ({
      name: badge?.name || 'n/a',
      recommendationStrategy: badge?.recommendationStrategy || 'n/a',
      recommendationVersion: badge?.recommendationVersion || 'n/a',
      type: badge?.type || 'price',
    }));
  }

  const tags = Array.isArray(choice?.tags) ? choice.tags.filter(Boolean) : [];
  if (tags.length > 0) {
    return tags.map((tag) => ({
      name: String(tag),
      recommendationStrategy: 'n/a',
      recommendationVersion: 'n/a',
      type: 'price',
    }));
  }

  return null;
};

const buildProductImpression = (choice, index) => {
  const sku = getChoiceSku(choice);
  if (!sku) {
    return null;
  }

  const badge = getBadgeInfo(choice);
  const productInfo = {
    customProduct: 'not custom catalog',
    position: Number(choice?.sequence) || index + 1,
    sku,
    sponsored: 'false',
    storeType: 'anchor',
    availabilityStatus: getAvailabilityStatus(choice)
  };

  if (badge) {
    productInfo.badge = badge;
  }

  return {
    productInfo
  };
};

export default (set, get, payload) => set(produce(get(), (draft) => {
  if (!draft.digitalData) {
    draft.digitalData = { event: [] };
  }

  const { categoryName, choices, filterEntryType } = payload;
  const productImpressions = (choices || [])
    .map((choice, index) => buildProductImpression(choice, index))
    .filter(Boolean);

  setProductImpressionsEmpty();
  setProductImpressions(productImpressions);

  const productImpressionsEvent = {
    category: { primaryCategory: 'overlay' },
    eventInfo: {
      eventName: 'product impressions',
      impressionCount: productImpressions.length,
    },
    content: {
      product: productImpressions,
    },
  };
  Analytics.sendAnalyticsEvent(productImpressionsEvent);

  const normalizedCategory = String(categoryName || 'select your category').toLowerCase();

  const displayAttributes = {
    productsAvailability: 'all products',
    sortBy: 'default',
    viewType: 'grid',
    pageNumber: '1',
  };

  const filterEntry = {
    filterType: 'primary',
    type: filterEntryType || 'add to your wall',
    value: normalizedCategory,
  };

  setAdditionalPageInfo('displayAttributes', displayAttributes);
  setAdditionalPageInfo('filter', filterEntry);

  Analytics.sendAnalyticsEvent({
    category: { primaryCategory: 'browse' },
    displayAttributes,
    filter: [[filterEntry]],
  });
}), false, { type: 'TRACK_PRODUCT_CATEGORY_SELECT' });
